import React, { useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  BookOpen,
  Megaphone,
  Printer,
  Globe2,
  Headphones,
  Sparkles,
  LayoutGrid,
  ChevronDown,
  LogOut,
  ShieldCheck,
  ShoppingBag,
  Search,
} from "lucide-react";
import { SERVICES } from "../lib/services";
import { useStudio } from "../context/StudioContext";
import { usePalette } from "../context/PaletteContext";
import { usePlanDrawer } from "../context/PlanDrawerContext";
import { useAdminAuth } from "../context/AdminAuthContext";
import BrandMark from "./BrandMark";

const ICONS = { BookOpen, Megaphone, Printer, Globe2, Headphones, Sparkles };

export default function Sidebar({ inSheet = false }) {
  const { email, plan, signOut } = useStudio();
  const { openPalette } = usePalette();
  const { openDrawer } = usePlanDrawer();
  const { me: adminMe } = useAdminAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(() => {
    // auto expand the active service group
    const match = SERVICES.find((s) => location.pathname.startsWith(`/service/${s.slug}`));
    return match ? match.slug : null;
  });

  const handleSignOut = () => {
    signOut();
    navigate("/");
  };

  return (
    <aside className={`sticky top-0 h-screen w-[260px] flex-none flex-col self-start border-r border-brand-hair bg-brand-panel ${inSheet ? "flex" : "hidden lg:flex"}`}>
      {/* Brand */}
      <div className="flex items-center gap-3 px-5 pb-5 pt-6">
        <BrandMark size={36} />
        <div className="leading-tight">
          <div className="font-serif-display text-[18px] font-semibold">
            Author<span className="italic text-brand">OS</span>
          </div>
          <div className="font-mono text-[9px] uppercase tracking-[0.28em] text-brand-mute">
            By Zebralearn
          </div>
        </div>
      </div>

      {/* Plan card */}
      <button
        onClick={openDrawer}
        className="mx-4 flex items-center justify-between rounded-md border border-brand-hair bg-brand-panel2 px-4 py-3 text-left transition-colors hover:border-brand/60"
      >
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-brand/10 text-brand">
            <ShoppingBag className="h-4 w-4" />
          </div>
          <div>
            <div className="font-mono text-[9px] uppercase tracking-[0.26em] text-brand-mute">
              Your Plan
            </div>
            <div className="font-serif-display text-[16px] leading-none text-brand-ivory">
              {plan.length === 0 ? "Empty" : `${plan.length} item${plan.length > 1 ? "s" : ""}`}
            </div>
          </div>
        </div>
        <ChevronDown className="h-4 w-4 -rotate-90 text-brand-mute" />
      </button>

      {/* Search launcher */}
      <button
        onClick={openPalette}
        className="mx-4 mt-3 flex items-center justify-between rounded-md border border-brand-hair bg-brand-panel2/60 px-3.5 py-2.5 text-left transition-colors hover:border-brand/60"
        aria-label="Open command palette"
      >
        <div className="flex items-center gap-2.5 text-brand-ivory/70">
          <Search className="h-3.5 w-3.5 text-brand-mute" />
          <span className="text-[12px]">Search studio…</span>
        </div>
        <span className="flex items-center gap-1">
          <kbd className="flex h-4 min-w-4 items-center justify-center rounded border border-brand-hair px-1 font-mono text-[9px] text-brand-mute">
            ⌘
          </kbd>
          <kbd className="flex h-4 min-w-4 items-center justify-center rounded border border-brand-hair px-1 font-mono text-[9px] text-brand-mute">
            K
          </kbd>
        </span>
      </button>

      {/* Sections */}
      <div className="mt-6 flex-1 overflow-y-auto px-3 pb-6">
        <div className="px-2 pb-2 font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">
          Dashboard
        </div>
        <NavLink
          to="/dashboard"
          className={({ isActive }) =>
            `mb-1 flex items-center gap-3 rounded-md px-3 py-2 text-[13px] transition-colors ${
              isActive
                ? "bg-brand/10 text-brand"
                : "text-brand-ivory/80 hover:bg-white/[0.04]"
            }`
          }
          end
        >
          <LayoutGrid className="h-4 w-4" />
          <span>Overview</span>
        </NavLink>

        <div className="mt-6 flex items-center justify-between px-2 pb-2 font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">
          <span>Services</span>
          <span className="text-brand-mute/70">0{SERVICES.length}</span>
        </div>

        <nav className="space-y-0.5">
          {SERVICES.map((svc) => {
            const Icon = ICONS[svc.icon] || BookOpen;
            const active = location.pathname.startsWith(`/service/${svc.slug}`);
            const isOpen = expanded === svc.slug;
            return (
              <div key={svc.slug}>
                <button
                  onClick={() => {
                    setExpanded(isOpen ? null : svc.slug);
                    navigate(`/service/${svc.slug}`);
                  }}
                  className={`flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-[13px] transition-colors ${
                    active ? "bg-brand/10 text-brand" : "text-brand-ivory/85 hover:bg-white/[0.04]"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span className="font-mono text-[10px] tracking-[0.22em] text-brand-mute">
                    {svc.id}
                  </span>
                  <span className="truncate text-[13px] font-medium leading-tight tracking-tight">
                    {svc.label}
                  </span>
                  <ChevronDown
                    className={`ml-auto h-3.5 w-3.5 text-brand-mute transition-transform ${
                      isOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {isOpen && (
                  <div className="mb-2 mt-0.5 space-y-0.5 pl-3">
                    {svc.subs.map((sub) => (
                      <NavLink
                        key={sub.code}
                        to={`/service/${svc.slug}/${sub.slug}`}
                        className={({ isActive }) =>
                          `flex items-center gap-3 rounded-md px-3 py-1.5 text-[12px] transition-colors ${
                            isActive
                              ? "bg-brand/10 text-brand"
                              : "text-brand-ivory/60 hover:bg-white/[0.03] hover:text-brand-ivory/90"
                          }`
                        }
                      >
                        <span className="font-mono text-[10px] tracking-[0.22em] text-brand-mute">
                          {sub.code}
                        </span>
                        <span className="truncate">{sub.title}</span>
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </div>

      {/* Footer */}
      <div className="border-t border-brand-hair px-5 py-4">
        {adminMe && (
          <button
            onClick={() => navigate("/admin")}
            data-testid="sidebar-admin-panel-btn"
            className="mb-3 flex w-full items-center justify-between gap-2 rounded-md border border-brand-hair bg-brand-panel2 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.28em] text-brand-ivory transition-colors hover:border-brand hover:text-brand"
            aria-label="Open admin console"
          >
            <span className="flex items-center gap-2">
              <ShieldCheck className="h-3.5 w-3.5" /> Admin Panel
            </span>
            <ChevronDown className="h-3.5 w-3.5 -rotate-90 text-brand-mute" />
          </button>
        )}
        <div className="pb-3 font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">
          Signed in as
        </div>
        <div className="truncate pb-3 text-[13px] text-brand-ivory">{email || "guest@authoros.dev"}</div>
        <button
          onClick={handleSignOut}
          className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute transition-colors hover:text-brand"
        >
          <LogOut className="h-3.5 w-3.5" /> Sign out
        </button>
      </div>
    </aside>
  );
}
